/**
 * recurrenceWrite.ts — the ONE way a repeating event gets created.
 *
 * Lifted out of `RecurrenceChip.handleSave` (TodayTab.tsx) so the Add-event
 * sheet's Repeat field uses the existing recurrence feature instead of a
 * second one. Nothing about the model changed; it moved.
 *
 * ── What a "repeat" is in Kin ──────────────────────────────────────────────
 *
 * A rule row in `recurrences` (weekly, by day-of-week, with an optional
 * until_date), plus MATERIALIZED rows in `events` — one real event row per
 * occurrence, tagged source_table='recurrences' / source_id=<rule id>. The
 * calendar never reads the rule; it reads the materialized rows like any other
 * event.
 *
 * That is why "repeat" cannot be a checkbox on an events row: there is no
 * recurrence FIELD to set. It is a second write to a second table.
 *
 * ⚠️ WEEKLY BY WEEKDAY IS THE WHOLE MODEL. There is no monthly, no yearly, no
 * every-N-weeks, and `day_of_week` being an int[] is what allows "every
 * weekday". Any caller offering an option outside that is promising something
 * the materializer cannot produce.
 *
 * ── Who writes the LATER occurrences ──────────────────────────────────────
 *
 * This function materializes 4 weeks. The `materialize-recurrences` Edge
 * Function, on the `materialize-recurrences-weekly` pg_cron job (0 3 * * 1
 * UTC), extends every active rule after that. So a rule saved here keeps going
 * without anything else being wired up — and a rule whose 4 weeks run out
 * while that cron is broken simply stops, which is the pre-existing behaviour.
 */

import { supabase } from "@/integrations/supabase/client";
import { formatLocalDate } from "./updateBarParser";
import { eventTimeColumns } from "./eventTimeSemantics";
import { reportError } from "./reportError";

/** How far ahead a newly-saved rule materializes. The weekly cron takes over
 *  from here; this is only the head start so the event shows up immediately. */
export const RECURRENCE_MATERIALIZE_DAYS = 28;

export interface RecurrenceDraft {
  title: string;
  /** 0=Sun..6=Sat. An array, so "every weekday" is [1,2,3,4,5]. */
  dayOfWeek: number[];
  /** "16:45", or null for no clock time. */
  startTime24: string | null;
  /** ⚠️ #607/#1359 — the field that separates all-day from TBD. `startTime24`
   *  is null for BOTH, so it cannot decide; `eventTimeColumns` reads this. */
  startTimeLabel: string;
  endTime24?: string | null;
  child: string | null;
  location: string | null;
  /** First day the rule applies, "YYYY-MM-DD". */
  startDate: string;
  /** Last day, or null for indefinite. */
  untilDate: string | null;
}

export interface SaveRecurrenceResult {
  /** The `recurrences` row id, or null when nothing was written. */
  ruleId: string | null;
  /** How many `events` rows were materialized now. */
  materialized: number;
  /** True when an identical active rule already existed — nothing was written. */
  alreadyExisted: boolean;
  error: unknown;
}

/** The dates a weekly rule covers between `from` and `from + days`. */
export function materializeDates(
  dayOfWeek: readonly number[],
  from: Date,
  untilDate: string | null,
  days: number = RECURRENCE_MATERIALIZE_DAYS,
): string[] {
  const start = new Date(from);
  start.setHours(0, 0, 0, 0);
  const horizon = new Date(start);
  horizon.setDate(start.getDate() + days);

  const out: string[] = [];
  for (const dow of dayOfWeek) {
    if (!Number.isInteger(dow) || dow < 0 || dow > 6) continue;
    const delta = (dow - start.getDay() + 7) % 7;
    const cursor = new Date(start);
    cursor.setDate(start.getDate() + delta);
    while (cursor <= horizon) {
      // ⚠️ formatLocalDate, NOT toISOString().slice(0,10). The original code
      // here used toISOString, which converts a LOCAL midnight to UTC: for any
      // positive-offset timezone that lands on the PREVIOUS calendar day, so
      // every materialized occurrence would be a day early. It never bit
      // because Kin's users are all on negative (US) offsets, where local
      // midnight is the same UTC date. Fixed on the way out rather than
      // carried into a module a second caller now depends on.
      const dateStr = formatLocalDate(cursor);
      if (!untilDate || dateStr <= untilDate) out.push(dateStr);
      cursor.setDate(cursor.getDate() + 7);
    }
  }
  return [...new Set(out)].sort();
}

/**
 * Creates the rule and its first 4 weeks of occurrences.
 *
 * ⚠️ The duplicate pre-check is not optional polish (#112). A first save can
 * surface an error toast even though both writes committed; a retry then
 * re-runs this and duplicates the rule AND every materialized event. An active
 * rule with the same user + title + day set is treated as already set up.
 *
 * `day_of_week` is an array column, so the day set is compared in JS rather
 * than relying on how array equality serializes through PostgREST.
 */
export async function saveRecurrence(
  userId: string,
  draft: RecurrenceDraft,
  now: Date = new Date(),
): Promise<SaveRecurrenceResult> {
  if (!userId || !draft.title.trim() || draft.dayOfWeek.length === 0) {
    return { ruleId: null, materialized: 0, alreadyExisted: false, error: null };
  }
  const wantedDays = [...draft.dayOfWeek].sort().join(",");

  try {
    const { data: existing } = await (supabase as any)
      .from("recurrences")
      .select("id, day_of_week")
      .eq("user_id", userId)
      .eq("title", draft.title)
      .eq("is_active", true);
    if (Array.isArray(existing)) {
      const hit = existing.find(
        (r: any) => [...(r.day_of_week ?? [])].sort().join(",") === wantedDays,
      );
      if (hit) {
        return { ruleId: hit.id, materialized: 0, alreadyExisted: true, error: null };
      }
    }
  } catch (e) {
    // Non-fatal: if the pre-check fails, fall through to the insert.
    reportError("[recurrenceWrite] dupe pre-check failed", e, { tags: { area: "recurrence" } });
  }

  const { data: rule, error: ruleErr } = await (supabase as any)
    .from("recurrences")
    .insert({
      user_id: userId,
      title: draft.title,
      day_of_week: draft.dayOfWeek,
      start_time: draft.startTime24 ? `${draft.startTime24}:00` : null,
      end_time: draft.endTime24 ? `${draft.endTime24}:00` : null,
      child: draft.child ?? null,
      location: draft.location ?? null,
      category: "event",
      start_date: draft.startDate,
      until_date: draft.untilDate ?? null,
      is_active: true,
    })
    .select()
    .single();
  if (ruleErr || !rule) {
    return {
      ruleId: null,
      materialized: 0,
      alreadyExisted: false,
      error: ruleErr ?? new Error("no rule returned"),
    };
  }

  // Materialize from the rule's OWN start date when that is in the future —
  // the Add-event sheet can create a rule starting on a day she tapped next
  // month, and anchoring to today would backfill occurrences before it begins.
  const anchor = (() => {
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    const ruleStart = new Date(`${draft.startDate}T00:00:00`);
    return !isNaN(ruleStart.getTime()) && ruleStart > today ? ruleStart : today;
  })();

  const rows = materializeDates(draft.dayOfWeek, anchor, draft.untilDate).map((dateStr) => ({
    user_id: userId,
    event_date: dateStr,
    title: draft.title,
    ...eventTimeColumns(draft.startTimeLabel, draft.startTime24),
    end_time: draft.endTime24 ? `${draft.endTime24}:00` : null,
    child: draft.child ?? null,
    location: draft.location ?? null,
    category: "event",
    source_type: "recurrence",
    source_table: "recurrences",
    source_id: rule.id,
    status: "active",
    is_prep: false,
    is_starred: false,
  }));

  if (rows.length > 0) {
    const { error: insertErr } = await (supabase as any).from("events").insert(rows);
    if (insertErr) {
      // The RULE landed but its occurrences did not, so the calendar shows
      // nothing until the weekly cron runs. Report the real state rather than
      // a plain success.
      return { ruleId: rule.id, materialized: 0, alreadyExisted: false, error: insertErr };
    }
  }

  return { ruleId: rule.id, materialized: rows.length, alreadyExisted: false, error: null };
}
